import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import commonStyles from "../../../style/common.module.css";
import { getCalendarSession, getSessionAttendance, updateSessionAttendance, SessionAttendanceEntry, SessionAttendanceDTO } from "../../service/session-attendance-service";
import { CalendarSession } from "../../model/calendar-session";
import ViewLoader from "../../../../component/misc/view-loader";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { Box, Button, Divider, FormControl, IconButton, InputLabel, MenuItem, Select, TextField, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useToast } from "../../../../context/toast/toast-provider";
import { getUsers } from "../../../-account/service/user-service";
import { getUserStudents } from "../../../-account/service/student-service";
import { User } from "../../../-account/model/user";
import { Role } from "../../../enum/role";

const STATUSES = ["Present", "Late", "Sick", "Excused", "Absent"];

export default function SessionAttendanceDetail() {
    const { cohortId, eventId } = useParams();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [session, setSession] = useState<CalendarSession | null>(null);
    const [attendees, setAttendees] = useState<SessionAttendanceDTO[]>([]);
    const [trainers, setTrainers] = useState<User[]>([]);
    const [, setStudents] = useState<User[]>([]);
    const [newGuideId, setNewGuideId] = useState<number | ''>('');
    const [newCoachId, setNewCoachId] = useState<number | ''>('');

    const toast = useToast();

    const guides = attendees.filter(a => a.sessionRole === "Guide");
    const coaches = attendees.filter(a => a.sessionRole === "Coach");
    const explorers = attendees.filter(a => a.sessionRole === "Explorer");

    useEffect(() => {
        if (eventId && cohortId) {
            setLoading(true);
            Promise.all([
                getCalendarSession(Number(cohortId), decodeURIComponent(eventId)),
                getSessionAttendance(Number(cohortId), decodeURIComponent(eventId)),
                getUsers([Role.Trainer, Role.LeadTrainer, Role.CohortCaptain, Role.Guide, Role.Coach]),
                getUserStudents(Number(cohortId))
            ]).then(([calSession, attendance, trainerList, studentList]) => {
                setSession(calSession);
                const existing = attendance?.attendees ?? [];
                setAttendees(existing);
                const allTrainers = trainerList ?? [];
                setTrainers(allTrainers);

                const studs = studentList ?? [];
                setStudents(studs);

                if (existing.length === 0) {
                    const entries: SessionAttendanceDTO[] = [];

                    // Parse guide/coach names from event description
                    const description = (calSession?.description ?? '').toLowerCase();
                    const guideEmails = new Set<string>();
                    const coachEmails = new Set<string>();

                    // Match patterns like "Guide: Name" or "Guides: Name1, Name2"
                    const guideMatch = description.match(/guides?\s*[:;]\s*([^\n]+)/i);
                    const coachMatch = description.match(/coach(?:es)?\s*[:;]\s*([^\n]+)/i);

                    const calendarAttendees = calSession?.attendees ?? [];

                    if (guideMatch) {
                        const guideNames = guideMatch[1].split(/[,&]/).map(n => n.trim().toLowerCase());
                        calendarAttendees.forEach(a => {
                            const name = (a.name ?? a.email.split('@')[0]).toLowerCase();
                            if (guideNames.some(g => name.includes(g) || g.includes(name))) {
                                guideEmails.add(a.email.toLowerCase());
                            }
                        });
                    }

                    if (coachMatch) {
                        const coachNames = coachMatch[1].split(/[,&]/).map(n => n.trim().toLowerCase());
                        calendarAttendees.forEach(a => {
                            const name = (a.name ?? a.email.split('@')[0]).toLowerCase();
                            if (coachNames.some(c => name.includes(c) || c.includes(name))) {
                                coachEmails.add(a.email.toLowerCase());
                            }
                        });
                    }

                    // Auto-add trainers from calendar attendees with role from description
                    const calendarEmails = calendarAttendees.map(a => a.email.toLowerCase());
                    const matchedTrainers = allTrainers.filter(t =>
                        t.email && calendarEmails.includes(t.email.toLowerCase())
                    );
                    matchedTrainers.forEach(t => {
                        const email = t.email!.toLowerCase();
                        const role = coachEmails.has(email) ? "Coach"
                            : guideEmails.has(email) ? "Guide"
                            : "Guide"; // default to Guide if no description match
                        entries.push({
                            id: 0, sessionId: 0, actorId: t.id,
                            actorName: t.name, sessionRole: role,
                            status: "Present", comment: undefined
                        });
                    });

                    // Auto-add cohort students as Explorers
                    studs.forEach(s => {
                        entries.push({
                            id: 0, sessionId: 0, actorId: s.id,
                            actorName: s.name, sessionRole: "Explorer",
                            status: "Present", comment: undefined
                        });
                    });

                    setAttendees(entries);
                }
                setLoading(false);
            });
        }
    }, [cohortId, eventId]);

    const addGuide = () => {
        if (newGuideId === '') return;
        if (attendees.find(a => a.actorId === newGuideId)) {
            toast.addToast("Already added", "warning");
            return;
        }
        const actor = trainers.find(a => a.id === newGuideId);
        setAttendees([...attendees, {
            id: 0, sessionId: 0, actorId: newGuideId,
            actorName: actor?.name ?? '', sessionRole: "Guide", status: "Present", comment: undefined
        }]);
        setNewGuideId('');
    };

    const addCoach = () => {
        if (newCoachId === '') return;
        if (attendees.find(a => a.actorId === newCoachId)) {
            toast.addToast("Already added", "warning");
            return;
        }
        const actor = trainers.find(a => a.id === newCoachId);
        setAttendees([...attendees, {
            id: 0, sessionId: 0, actorId: newCoachId,
            actorName: actor?.name ?? '', sessionRole: "Coach", status: "Present", comment: undefined
        }]);
        setNewCoachId('');
    };

    const handleRemove = (actorId: number) => {
        setAttendees(attendees.filter(a => a.actorId !== actorId));
    };

    const handleStatusChange = (actorId: number, status: string) => {
        setAttendees(attendees.map(a => a.actorId === actorId ? { ...a, status } : a));
    };

    const handleCommentChange = (actorId: number, comment: string) => {
        setAttendees(attendees.map(a => a.actorId === actorId ? { ...a, comment } : a));
    };

    const handleSave = () => {
        if (!cohortId || !eventId) return;
        setSubmitting(true);
        const entries: SessionAttendanceEntry[] = attendees.map(a => ({
            actorId: a.actorId,
            sessionRole: a.sessionRole,
            status: a.status,
            comment: a.comment
        }));
        updateSessionAttendance(Number(cohortId), decodeURIComponent(eventId), { attendees: entries })
            .then(result => {
                setAttendees(result.attendees);
                toast.addToast("Attendance saved", "success");
                setSubmitting(false);
            })
            .catch(() => {
                toast.addToast("Failed to save attendance", "error");
                setSubmitting(false);
            });
    };

    const makeColumns = (canRemove: boolean): GridColDef[] => [
        { field: 'actorName', headerName: 'Name', flex: 1.5 },
        {
            field: 'status', headerName: 'Status', flex: 0.8,
            renderCell: (params: GridRenderCellParams) => (
                <Select
                    size="small"
                    value={params.value}
                    onChange={(e) => handleStatusChange(params.row.actorId, e.target.value)}
                    variant="standard"
                    fullWidth
                >
                    {STATUSES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                </Select>
            )
        },
        {
            field: 'comment', headerName: 'Comment', flex: 1.5,
            renderCell: (params: GridRenderCellParams) => (
                <TextField
                    size="small"
                    variant="standard"
                    value={params.value ?? ''}
                    onChange={(e) => handleCommentChange(params.row.actorId, e.target.value)}
                    fullWidth
                    placeholder="Add comment..."
                />
            )
        },
        ...(canRemove ? [{
            field: 'actions', headerName: '', flex: 0.3, sortable: false,
            renderCell: (params: GridRenderCellParams) => (
                <IconButton size="small" onClick={() => handleRemove(params.row.actorId)}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )
        }] : [])
    ];

    return (
        <div className={commonStyles.content}>
            <ViewLoader loading={loading} />
            {!loading && session && (
                <>
                    <Typography variant="h5">{session.title}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        {new Date(session.start).toLocaleDateString()} &nbsp;
                        {session.startTime} - {session.endTime}
                    </Typography>

                    {/* GUIDES SECTION */}
                    <Typography variant="h6" sx={{ mb: 1 }}>Guides</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Add Guide</InputLabel>
                            <Select value={newGuideId} label="Add Guide" onChange={(e) => setNewGuideId(e.target.value as number)}>
                                {trainers
                                    .filter(t => !attendees.find(a => a.actorId === t.id))
                                    .map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button variant="outlined" size="small" onClick={addGuide} disabled={newGuideId === ''}>Add</Button>
                    </Box>
                    <DataGrid
                        rows={guides}
                        columns={makeColumns(true)}
                        getRowId={(row) => row.actorId}
                        autoHeight
                        disableRowSelectionOnClick
                        hideFooter={guides.length <= 10}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* COACHES SECTION */}
                    <Typography variant="h6" sx={{ mb: 1 }}>Coaches</Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 200 }}>
                            <InputLabel>Add Coach</InputLabel>
                            <Select value={newCoachId} label="Add Coach" onChange={(e) => setNewCoachId(e.target.value as number)}>
                                {trainers
                                    .filter(t => !attendees.find(a => a.actorId === t.id))
                                    .map(t => <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Button variant="outlined" size="small" onClick={addCoach} disabled={newCoachId === ''}>Add</Button>
                    </Box>
                    <DataGrid
                        rows={coaches}
                        columns={makeColumns(true)}
                        getRowId={(row) => row.actorId}
                        autoHeight
                        disableRowSelectionOnClick
                        hideFooter={coaches.length <= 10}
                    />

                    <Divider sx={{ my: 2 }} />

                    {/* EXPLORERS/STUDENTS SECTION */}
                    <Typography variant="h6" sx={{ mb: 1 }}>
                        Explorers ({explorers.filter(e => e.status === "Present").length}/{explorers.length})
                    </Typography>
                    <DataGrid
                        rows={explorers}
                        columns={makeColumns(false)}
                        getRowId={(row) => row.actorId}
                        autoHeight
                        disableRowSelectionOnClick
                        hideFooter={explorers.length <= 25}
                        initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
                    />

                    <Box sx={{ mt: 3 }}>
                        <Button variant="contained" onClick={handleSave} disabled={submitting}>
                            Save Attendance
                        </Button>
                    </Box>
                </>
            )}
            {!loading && !session && <Typography>Session not found</Typography>}
        </div>
    );
}
